import {
  Box,
  Icon,
  IconButton,
  Snackbar,
  SnackbarProps,
  Theme,
  Typography,
} from "@outschool/backpack";
import { faAngleRight, faX } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { onboardingQuizPath } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React, { useEffect, useRef } from "react";

export const OnboardingSnackBar = ({
  waitingDuration = 5000,
  ...props
}: { waitingDuration?: number } & SnackbarProps) => {
  const [open, setOpen] = React.useState(false);
  const hasRunOnceRef = useRef(false);

  const navigate = useNavigation();
  const { t } = useTranslation(
    "ui-components-website\\Snackbar\\OnboardingSnackbar",
  );

  useEffect(() => {
    if (hasRunOnceRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (!open) {
        setOpen(true);
      }
      hasRunOnceRef.current = true;
    }, waitingDuration);

    return () => clearTimeout(timer);
  }, [open, waitingDuration, hasRunOnceRef]);

  const onClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };
  return (
    <Snackbar
      {...props}
      open={open}
      onClose={onClose}
      sx={(theme: Theme) => ({
        background:
          "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.25) 100%)",
        padding: "8px",
        position: "fixed",
        bottom: "0 !important",
        width: "100%",
        left: 0,
        right: 0,
        [theme.breakpoints.up("md")]: { padding: "16px" },
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          maxWidth: "1088px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
          borderRadius: ".5rem",
          gap: "8px",
          padding: "8px 16px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.5)",
          backgroundImage: `url("https://static2.outschool.com/master/public/images/purpleDots.webp"), url("https://static2.outschool.com/master/public/images/yellowPatternBg.webp"), ${theme.gradients?.primarySecondary}`,
          backgroundPosition: "-40% center, 130% center, center",
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "contain, contain, cover",
          backgroundClip: "padding-box",
          [theme.breakpoints.up("md")]: {
            width: "100%",
            backgroundPosition: "center left, center right, center",
            flexDirection: "row",
            borderRadius: "1rem",
            gap: "12px",
            padding: "16px",
            margin: "0 1rem",
          },
        })}
      >
        <Typography
          emphasized
          sx={(theme: Theme) => ({
            typography: "subtitle2",
            ...theme.typography.emphasized,
            [theme.breakpoints.up("md")]: {
              typography: "display3",
            },
          })}
        >
          {t("Want personalized results today?")}
        </Typography>
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            gap: "8px",
            [theme.breakpoints.up("md")]: { gap: "12px" },
          })}
        >
          <TrackedButton
            size="small"
            variant="contained"
            onClick={() => navigate(onboardingQuizPath(), { hardNav: true })}
            trackingName="onboarding_snackbar_cta"
            aria-label={t("See Matches")}
            sx={(theme: Theme) => ({
              [theme.breakpoints.up("md")]: {
                size: "large",
              },
            })}
          >
            {t("See Matches")}
            <Icon icon={faAngleRight} />
          </TrackedButton>
          <IconButton
            icon={faX}
            onClick={onClose}
            aria-label={t("Close")}
            variant="text"
            sx={{ color: "common.black" }}
          />
        </Box>
      </Box>
    </Snackbar>
  );
};
